import AppBarStandard from '@/Elements/AppBarStandard';
import BookingSummary from '@/Elements/BookingSummary';
import ChildrenDataTable from '@/Elements/ChildrenDataTable';
import MainContainer from '@/Elements/MainContainer';
import Navbar from '@/Elements/Navbar';
import { useAuth } from '@/Services/AuthContext';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import Arc from '@/lib/Arc';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';



const BookingScreen = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(new Date())
    const { user, isAuthenticated, loading, login, logout, invalidate } = useAuth();
    const [childrenData, setChildrenData] = useState(user.children);
    const [selectedChildren, setSelectedChildren] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [numberOfAdults, setNumberOfAdults] = useState(null);

    const generateTimeHoursList = () => {
        const timeList = [];
        for (let i = 10; i <= 21; i++) { // 10 AM (10) to 9 PM (21)
            let hour = i;
            let ampm = "AM";

            if (hour >= 12) {
                ampm = "PM";
                if (hour > 12) {
                    hour -= 12;
                }
            }

            timeList.push({ slot: i, label: `${hour} ${ampm}` });
        }
        return timeList;
    };

    const handleConfirmBooking = () => {
        if (!selectedTimeSlot) {
            toast.error("Please select a time slot.");
            return;
        }
        if (!selectedDuration) {
            toast.error("Please select a duration.");
            return;
        }
        if (!numberOfAdults) {
            toast.error("Please select the number of accompanying adults.");
            return;
        }
        if (selectedChildren.length === 0) {
            toast.error("Please select at least one child.");
            return;
        }

        const bookingData = {
            date: date.toLocaleDateString(),
            timeSlot: selectedTimeSlot,
            duration: selectedDuration,
            numberOfAdults: numberOfAdults,
            selectedChildren: selectedChildren,
            guardian: user,
        };
        console.log("Booking Data:", bookingData);
        toast.success("Booking confirmed! (Data logged to console)");
        // Here you would typically send this data to your backend

        // Map bookingData to the required structure for Arc.serviceCall
        const formattedBookingData = {
            guardian_uid: user.uid,
            booking_date: date.toISOString().split('T')[0], // Format to YYYY-MM-DD
            time_slot_start: `${String(selectedTimeSlot).padStart(2, '0')}:00:00`, // e.g., "12:00:00"
            time_slot_end: `${String(selectedTimeSlot + selectedDuration).padStart(2, '0')}:00:00`, // e.g., "14:00:00"
            amount_paid: 0, // Placeholder, as not provided in current UI/logic
            child_ids: selectedChildren.map(child => child.uid).join(','), // Comma-separated UIDs
            booking_status: 'Pending', // Default status
            payment_status: 'Unpaid', // Default payment status
            payment_method: 'Online', // Default payment method
            gstin: null, // Not collected in UI
            data: {
                numberOfAdults: numberOfAdults, // Store additional data in 'data' field
            },
        };

        console.log("Formatted Booking Data for API:", formattedBookingData);

        // Assuming Arc is imported or globally available (e.g., import Arc from '@/Services/Arc';)
        Arc().serviceCall('booking', 'createBooking', [formattedBookingData])
            .then(res => {
                toast.success('Booking created successfully!');
                console.log('Booking API Response:', res);
                // Optionally navigate or clear form after successful booking
                navigate('/booking/history');
            })
            .catch(error => {
                console.error('Failed to create booking:', error);
                toast.error('Oops! Failed to create booking.');
            });

    };


    return (<>
        <Navbar ></Navbar>

        <MainContainer>
            <AppBarStandard options={[(<Button variant="ghost" onClick={() => { navigate('/home') }}>
                <Plus />
            </Button>)]
            } title="Scheduling Fun Is Easy" description="True Async PHP is Here! First Alpha Release Unleashes a New Era for High-Performance Apps.">
            </AppBarStandard>
            <div className='mt-10 flex w-full flex-col lg:flex-row'>
                <div className='flex flex-col w-full lg:w-2/3'>
                    <div className='bg-accent/30  rounded-lg p-1 flex items-center'>
                        <div className='h-8 w-8 bg-accent rounded mr-2 font-bold flex items-center justify-center'>
                            1
                        </div>
                        Choose a date and time.
                    </div>
                    <div className='flex w-full gap-10 mt-4 flex-col md:flex-row'>
                        <div className='flex flex-col shrink-0'>
                            <Label className="">When are you arriving?</Label>
                            <Calendar
                                mode="single"
                                defaultMonth={new Date()} // Set default month to today's date
                                selected={date}
                                onSelect={(selectedDate) => {
                                    const finalDate = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0); // Normalize today to the start of the day

                                    if (!selectedDate) {
                                        // If no date is selected (e.g., clearing selection), keep the current date.
                                        console.log("No date selected, keeping current date.");
                                        return;
                                    }

                                    // If selected date is before today, reset to today and stop further processing.
                                    if (selectedDate < today) {
                                        setDate(today);
                                        console.log("Selected date is before today, resetting to today:", today);
                                        toast.error("Select a date thats today or after today.");
                                        return;
                                    }

                                    // If selected date is greater than finalDate, keep the current date (by returning).
                                    if (selectedDate > finalDate) {
                                        console.log("Selected date is after final date, keeping current date.");
                                        toast.error("Bookings are only accepted for 1 month in advance.");
                                        return;
                                    }
                                    setDate(selectedDate);
                                    console.log("Selected Date:", selectedDate); // Log the selected date
                                }}
                                className="rounded-lg border shadow-sm mt-2 border-muted"
                                numberOfMonths={1} // Show current month and next month
                                fromDate={new Date()} // Start selectable from today
                                toDate={new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0)} // End selectable at the end of the next month
                            />
                            <p className='text-sm text-muted-foreground w-full overflow-clip mt-1'>
                                Current selected date is <b>{date.toLocaleDateString()}</b>
                            </p>
                        </div>
                        <div className='flex flex-col grow'>
                            <Label className="">When are you arriving?</Label>
                            <Select onValueChange={setSelectedTimeSlot} value={selectedTimeSlot}>
                                <SelectTrigger className="w-4/5 mt-2 bg-white dark:bg-black/30" variant="ghost">
                                    <SelectValue placeholder="Select a time slot" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>Start playtime by</SelectLabel>
                                        {generateTimeHoursList().map(v => <SelectItem value={v.slot} key={v.slot}>{v.label}</SelectItem>)}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <p className='text-sm text-muted-foreground'>
                                This is a line description for this field.
                            </p>

                            <Label className="mt-8">How long will you be staying?</Label>
                            <Select onValueChange={setSelectedDuration} value={selectedDuration}>
                                <SelectTrigger className="w-4/5 mt-2 bg-white dark:bg-black/30" variant="ghost">
                                    <SelectValue placeholder="Select a time period" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>Will play for</SelectLabel>
                                        {[...Array(5).keys()].map(i => <SelectItem key={i + 1} value={i + 1}>{i + 1} {i == 0 ? 'Hr' : 'Hrs'} </SelectItem>)}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <p className='text-sm text-muted-foreground'>
                                This is a line description for this field.
                            </p>

                            <Label className="mt-8">How Many adults will be accompaying</Label>
                            <Select onValueChange={setNumberOfAdults} value={numberOfAdults}>
                                <SelectTrigger className="w-4/5 mt-2 bg-white dark:bg-black/30" variant="ghost">
                                    <SelectValue placeholder="Select the number of people" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectLabel>Will play for</SelectLabel>
                                        {[...Array(5).keys()].map(i => <SelectItem key={i + 1} value={i + 1}>{i + 1} {i == 0 ? 'Person' : 'People'} </SelectItem>)}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <p className='text-sm text-muted-foreground'>
                                This is a line description for this field.
                            </p>
                        </div>
                    </div>
                    <div className='bg-accent/30  rounded-lg p-1 flex items-center mt-10'>
                        <div className='h-8 w-8 bg-accent rounded mr-2 font-bold flex items-center justify-center'>
                            2
                        </div>
                        Children who will be playing.
                    </div>
                    <ChildrenDataTable manualSave={true} guardian={user} childrenData={childrenData} setChildrenData={setChildrenData} onSaveCallback={console.log} onSelectedChange={setSelectedChildren}></ChildrenDataTable>
                </div>

                <div className='w-full lg:w-1/3 lg:pl-10'>
                    <div className=' md:top-24 md:sticky'>
                        <div className='bg-accent/30  rounded-lg p-1 flex items-center'>
                            <div className='h-8 w-8 bg-accent rounded mr-2 font-bold flex items-center justify-center'>
                                3
                            </div>
                            Payment & Booking Confirmation
                        </div>
                        <BookingSummary
                            selectedChildren={selectedChildren}
                            bookingDetails={{
                                date: date,
                                timeSlot: selectedTimeSlot,
                                duration: selectedDuration,
                                numberOfAdults: numberOfAdults,
                            }}
                            onConfirmBooking={handleConfirmBooking}
                        />
                    </div>

                </div>
            </div>
        </MainContainer>

    </>)
};
export default BookingScreen;